import tailwind from '../../../tailwind.config.cjs';

const MOBILE_INSTANT_VIEW_SELECTOR = [
  'form[data-view]',
  '.filters-sec__search[data-view]',
  '.filters-sec__selects[data-view]',
  '.search-sec__filters-toggle[data-view]',
].join(', ');

const shouldDisableMobileViewAnimation = (element, viewportWidth = window.innerWidth) => (
  viewportWidth < 768
  && (
    element.dataset.viewDisableMobile === 'true'
    || element.matches(MOBILE_INSTANT_VIEW_SELECTOR)
  )
);

function revealWithoutViewAnimation(element) {
  element.style.transition = 'none';
  element.classList.add('animated');
}

export function initAnimationOnView(options = {}) {
  const defaultOptions = {
    animateOnce: true,
    speed: 600,
    threshold: 0.35,
    delay: 0,
    easing: 'ease',
    disableMutationObserver: false,
    breakpoints: {},
  };

  if (Object.keys(tailwind.theme.screens).length) {
    defaultOptions.breakpoints = tailwind.theme.screens;
  }

  const { breakpoints } = defaultOptions;
  const mergedBreakpoints = Object.assign(breakpoints, options.breakpoints);
  const mainOptions = Object.assign(defaultOptions, options);
  mainOptions.breakpoints = mergedBreakpoints;

  function setGlobalProperty(name, value, isTimeUnit) {
    if (isTimeUnit) value += 'ms';
    document.documentElement.style.setProperty(`--view-${name}`, `${value}`);
  }

  const { speed, easing } = mainOptions;
  setGlobalProperty('speed', speed, true);
  setGlobalProperty('easing', easing);

  /* --- Observer Pool: один IntersectionObserver на уникальную комбинацию threshold и offset --- */
  const observerPool = new Map();

  function getThreshold(element) {
    const dataset = element.dataset;
    const desktopThreshold = dataset.viewThreshold
      ? parseFloat(dataset.viewThreshold)
      : mainOptions.threshold;
    const mobileThreshold = dataset.viewThresholdMobile
      ? parseFloat(dataset.viewThresholdMobile)
      : desktopThreshold;
    return window.innerWidth < 768 ? mobileThreshold : desktopThreshold;
  }

  function checkMediaCondition(element) {
    const { viewMinMedia, viewMedia } = element.dataset;
    const width = window.innerWidth;

    if (viewMinMedia && width <= parseInt(viewMinMedia, 10)) return false;
    if (viewMedia && width > parseInt(viewMedia, 10)) return false;

    return true;
  }

  function animateElement(element, observer) {
    if (!checkMediaCondition(element)) return;

    if (shouldDisableMobileViewAnimation(element)) {
      revealWithoutViewAnimation(element);

      if (observer) observer.unobserve(element);
      return;
    }

    const duration = element.dataset.viewSpeed ? parseInt(element.dataset.viewSpeed, 10) : speed;

    element.style.willChange = 'transform, opacity, filter';
    element.classList.add('animated');

    if (mainOptions.animateOnce) {
      if (observer) observer.unobserve(element);
      setTimeout(() => {
        element.style.willChange = 'auto';
      }, duration + 100);
    }
  }

  /* --- Throttled Resize for Media Queries --- */
  let resizeTimeout;
  function handleResize() {
    if (resizeTimeout) clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      document.querySelectorAll('[data-view]:not(.animated)').forEach((el) => {
        const isMatched = checkMediaCondition(el);
        const hasInView = el.classList.contains('in-view');

        if (isMatched && !hasInView) {
          intersectElement(el);
        } else if (!isMatched && hasInView) {
          el.classList.remove('in-view');
          el.style.transitionDuration = '';
          el.style.transitionTimingFunction = '';

          const threshold = getThreshold(el);
          const offset = el.dataset.viewOffset || 0;
          const observer = getOrCreateObserver(threshold, offset);
          observer.unobserve(el);
        } else if (isMatched && hasInView) {
          const rect = el.getBoundingClientRect();
          const offset = parseInt(el.dataset.viewOffset, 10) || 0;
          const isInViewport = rect.top < (window.innerHeight - offset) && rect.bottom > 0;

          if (isInViewport) {
            const threshold = getThreshold(el);
            const observer = getOrCreateObserver(threshold, offset);
            animateElement(el, observer);
          }
        }
      });
    }, 150);
  }

  window.addEventListener('resize', handleResize);

  function getOrCreateObserver(threshold, offset) {
    const key = `${threshold}-${offset}`;
    if (observerPool.has(key)) {
      return observerPool.get(key);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            animateElement(entry.target, observer);
          } else if (!entry.isIntersecting && !mainOptions.animateOnce) {
            entry.target.classList.remove('animated');
            entry.target.style.willChange = 'transform, opacity, filter';
          }
        });
      },
      {
        threshold,
        rootMargin: `0px 0px -${offset}px 0px`
      }
    );

    observerPool.set(key, observer);
    return observer;
  }

  function intersectElement(element) {
    if (!checkMediaCondition(element)) {
      return;
    }

    element.classList.add('in-view');
    const dataset = element.dataset;

    if (shouldDisableMobileViewAnimation(element)) {
      revealWithoutViewAnimation(element);
      return;
    }

    if (dataset.viewSpeed) {
      element.style.transitionDuration = `${dataset.viewSpeed}ms`;
    }

    if (dataset.viewEasing) {
      element.style.transitionTimingFunction = dataset.viewEasing;
    }

    const threshold = getThreshold(element);
    const offset = dataset.viewOffset ? parseInt(dataset.viewOffset, 10) : 0;
    const observer = getOrCreateObserver(threshold, offset);
    const delay = dataset.viewDelay ? parseInt(dataset.viewDelay, 10) : 0;

    if (delay > 0) {
      setTimeout(() => observer.observe(element), delay);
    } else {
      observer.observe(element);
    }
  }

  const targetElements = document.querySelectorAll('[data-view]');
  if (targetElements.length) {
    targetElements.forEach((target) => intersectElement(target));
  }

  if (!mainOptions.disableMutationObserver) {
    const handleMutations = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((target) => {
            if (target.dataset && 'view' in target.dataset) {
              intersectElement(target);
            }
          });
        }
      }
    };

    const mutationObserver = new MutationObserver(handleMutations);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: false,
    });
  }
}

initAnimationOnView();

function handleInstantElements() {
  document.querySelectorAll('[data-view-instant="true"]').forEach((element) => {
    element.classList.add('animated');
  });
}

handleInstantElements();

// data-view-threshold="0.9"
// data-view-threshold-mobile="0.1"
// data-view-disable-mobile="true"
// data-view-instant="true"
// data-view-min-media="951"
// data-view-media="950"
